import * as ko from 'knockout';
import { GameBase } from '../../shared/models/game-base';
import type { ITeam } from '../../shared/interfaces/ITeam';
import { SelectionType } from '../../shared/enums/selection-type';

/**
 * NHL Game.
 * - Supports OT and SO selection types (HOME_WIN_OVERTIME, HOME_WIN_SHOOTOUT,
 *   AWAY_WIN_OVERTIME, AWAY_WIN_SHOOTOUT) in addition to standard WIN types.
 * - Adds dropdown CSS/text computed properties for the OT/SO UI controls.
 */
export class NhlGame extends GameBase {
  dropdownTextAway: KnockoutComputed<string>;
  dropdownTextHome: KnockoutComputed<string>;
  dropdownCssAway: KnockoutComputed<string>;
  dropdownCssHome: KnockoutComputed<string>;

  constructor(
    home: KnockoutObservable<ITeam> | ITeam,
    away: KnockoutObservable<ITeam> | ITeam,
    week: number,
    gameScheduleNum?: number,
    primeTimeType?: number,
    gameDate?: string,
    gameGuid?: string,
    gameKey?: string
  ) {
    super({
      home: home as KnockoutObservable<ITeam>,
      away: away as KnockoutObservable<ITeam>,
      week,
      gameScheduleNum,
      primeTimeNum: primeTimeType,
      gameDate,
      gameGuid,
      gameKey,
    });

    this.dropdownTextAway = ko.pureComputed(() => {
      if (this.selectionMade() && this.selectionType() !== SelectionType.NONE) {
        switch (this.selectionType()) {
          case SelectionType.AWAY_WIN_OVERTIME:
            return 'OT';
          case SelectionType.AWAY_WIN_SHOOTOUT:
            return 'SO';
          default:
            return 'OT/SO';
        }
      }
      return 'OT/SO';
    });

    this.dropdownCssAway = ko.pureComputed(() => {
      if (this.selectionMade() && this.selectionType() !== SelectionType.NONE) {
        switch (this.selectionType()) {
          case SelectionType.AWAY_WIN_OVERTIME:
          case SelectionType.AWAY_WIN_SHOOTOUT:
            return this.teamSelected();
          default:
            return '';
        }
      }
      return '';
    });

    this.dropdownTextHome = ko.pureComputed(() => {
      if (this.selectionMade() && this.selectionType() !== SelectionType.NONE) {
        switch (this.selectionType()) {
          case SelectionType.HOME_WIN_OVERTIME:
            return 'OT';
          case SelectionType.HOME_WIN_SHOOTOUT:
            return 'SO';
          default:
            return 'OT/SO';
        }
      }
      return 'OT/SO';
    });

    this.dropdownCssHome = ko.pureComputed(() => {
      if (this.selectionMade() && this.selectionType() !== SelectionType.NONE) {
        switch (this.selectionType()) {
          case SelectionType.HOME_WIN_OVERTIME:
          case SelectionType.HOME_WIN_SHOOTOUT:
            return this.teamSelected();
          default:
            return '';
        }
      }
      return '';
    });
  }

  protected override createGameClass(): string {
    let retStr = '';
    if (this.selectionMade() && this.TeamThatWon()) {
      retStr += 'selection-made ';
      if (this.selectionType() !== SelectionType.NONE) {
        retStr += this.TeamThatWon()().TeamAbbreviation;
      }
    }
    return retStr;
  }

  protected override createTeamThatWon(): KnockoutObservable<ITeam> | null {
    if (this.selectionMade() && this.selectionType() !== SelectionType.NONE) {
      switch (this.selectionType()) {
        case SelectionType.AWAY_WIN_OVERTIME:
        case SelectionType.AWAY_WIN_SHOOTOUT:
        case SelectionType.AWAY_WIN:
          return this.away;
        case SelectionType.HOME_WIN_OVERTIME:
        case SelectionType.HOME_WIN_SHOOTOUT:
        case SelectionType.HOME_WIN:
          return this.home;
        default:
          return null;
      }
    }
    return null;
  }

  protected override createTeamThatLost(): KnockoutObservable<ITeam> | null {
    if (this.selectionMade() && this.selectionType() !== SelectionType.NONE) {
      switch (this.selectionType()) {
        case SelectionType.AWAY_WIN_OVERTIME:
        case SelectionType.AWAY_WIN_SHOOTOUT:
        case SelectionType.AWAY_WIN:
          return this.home;
        case SelectionType.HOME_WIN_OVERTIME:
        case SelectionType.HOME_WIN_SHOOTOUT:
        case SelectionType.HOME_WIN:
          return this.away;
        default:
          return null;
      }
    }
    return null;
  }
}
